import {Guid} from 'src/app/models/guid';
import {AdvancedFieldValue} from '../models/advanced-field-value';
import {SavedFilter} from '../models/saved-filter';
import {FilterField} from '../models/filter-field';
import {FieldDisplayType} from '../models/field-display-type';
import {DynamicChoiceRepository} from './dynamic-choice-repository';
import {SavedFilterField, SavedFilterFieldFactory} from '../models/saved-filter-field';
import {BuiltInFieldId} from 'src/app/models/built-in-field-id.enum';
import {SelectFilterComponent} from '../../select-filter/select-filter.component';
import {FilterComparerGuids} from '../models/filter-comparer-guids';

export class AdvancedSelectionManager {
    selectedFields: AdvancedFieldValue[] = [];
    private selectedReportUniqueFilterHits: Set<string> = new Set<string>();

    /** Number of fields participating in a group. */
    groupCnt = 0;

    /** Number of groups that have a NOT. */
    notGroupCnt = 0;

    constructor(private choiceRepo: DynamicChoiceRepository) { }

    public optionSelected(option: any) {
        if (option.fields) option.fields.forEach(f => this.addField(new AdvancedFieldValue(f))); // An entire group was selected
        else this.addField(new AdvancedFieldValue(option));
        this.setTitleColspans();
    }

    public addField(field: AdvancedFieldValue): void {
        // Dynamic drop-downs and multi-selects need extra data - go get it
        if (field.field.displayType === FieldDisplayType.MultiSelect) this.choiceRepo.loadChoicesFor(field.field);

        this.selectedFields.push(field);
    }

    public addToGroup(idx: number): void {
        // Add a placeholder field for the field search
        this.selectedFields.splice(idx + 1, 0, undefined);

        if (!this.selectedFields[idx].group) {
            // First member of group
            this.selectedFields[idx].group = Guid.newGuid();
            this.selectedFields[idx].groupLength = 2;
            this.groupCnt++;
        } else {
            // Adding to existing group - find first field in group and update groupLength
            this.adjustGroupLength(this.selectedFields[idx].group, 1);
        }

        this.setTitleColspans();

        this.groupCnt++;
    }

    public toggleGroupNot(idx: number): void {
        if (this.selectedFields[idx].groupNot) {
            this.selectedFields[idx].groupNot = false;
            this.notGroupCnt--;
        } else {
            this.selectedFields[idx].groupNot = true;
            this.notGroupCnt++;
        }

        this.setTitleColspans();
    }

    public removeAt(idx: number): void {
        // if removing the field search, use the group above. Otherwise, use current field group (which may be undefined if not in group)
        const group = this.selectedFields[idx] === undefined ? this.selectedFields[idx - 1].group : this.selectedFields[idx].group;
        if (group) {
            let groupStartIdx = this.adjustGroupLength(group, -1);
            if (groupStartIdx !== -1) {
                if (groupStartIdx === idx) {
                    // First field in group is being removed - move groupLength & groupNot 'down'
                    this.selectedFields[groupStartIdx + 1].groupLength = this.selectedFields[groupStartIdx].groupLength;
                    this.selectedFields[groupStartIdx + 1].groupNot = this.selectedFields[groupStartIdx].groupNot;
                    groupStartIdx++;
                }

                // If last field in group, remove group
                if (this.selectedFields[groupStartIdx].groupLength === 1) {
                    this.selectedFields[groupStartIdx].groupLength = undefined;
                    this.selectedFields[groupStartIdx].group = undefined;
                    this.groupCnt--;

                    // If the group had a not, remove it
                    if (this.selectedFields[groupStartIdx].groupNot) {
                        this.selectedFields[groupStartIdx].groupNot = false;
                        this.notGroupCnt--;
                    }
                }

                this.groupCnt--;
            }
        }

        const removedField = this.selectedFields[idx];
        if (removedField?.field.displayType === FieldDisplayType.SearchReport) {
            const removedKey = this.formatSearchReportUniqueKey(removedField.field.fieldId, idx);
            this.selectedReportUniqueFilterHits.delete(removedKey);
        }

        // Adjust the unique keys for subsequent SearchReport fields
        this.selectedFields.forEach((field, index) => {
            if (field.field.displayType === FieldDisplayType.SearchReport && index > idx) {
                const oldKey = this.formatSearchReportUniqueKey(field.field.fieldId, index);
                const newKey = this.formatSearchReportUniqueKey(field.field.fieldId, index - 1);

                if (this.selectedReportUniqueFilterHits.has(oldKey)) {
                    this.selectedReportUniqueFilterHits.delete(oldKey);
                    this.selectedReportUniqueFilterHits.add(newKey);
                }
            }
        });
            
        this.selectedFields.splice(idx, 1);
        this.setTitleColspans();
    }

    public optionSelectedGroup(option: any, idx: number): void {
        // Replace field search with actual field
        if (option.fields) { // An entire group was selected
            for (let index = 0; index < option.fields.length; index++) {
                // The first field needs to remove the field search - the rest just need to be added
                this.selectedFields.splice(idx, index === 0 ? 1 : 0, new AdvancedFieldValue(option.fields[index], this.selectedFields[idx - 1].group));
            }

            // 1 was already added to the groupLength for the group when the field search was added - add the rest
            this.adjustGroupLength(this.selectedFields[idx - 1].group, option.fields.length - 1);
        } else {
            this.selectedFields.splice(idx, 1, new AdvancedFieldValue(option, this.selectedFields[idx - 1].group));
        }

        this.setTitleColspans();
    }

    public deserialize(fields: FilterField[], json: SavedFilter): void {
        const fieldsById = new Map<string, FilterField>();
        fields.forEach(f => fieldsById.set(f.fieldId, f));

        let curGroup: string;
        json.fields.forEach(f => {
            if (!fieldsById.has(f.fieldId)) {
                console.warn(`${f.fieldId} is in filter JSON but not in set of possible fields. Field will not appear in UI.`);
                return;
            }

            const newField = new AdvancedFieldValue(fieldsById.get(f.fieldId));
            newField.comparer = f.comparer;
            newField.value = f.value;
            newField.group = f.group;

            if (f.fieldId === BuiltInFieldId.DocSource || f.fieldId === BuiltInFieldId.DocType) {
                for (let index = 0; index < f.value.length; index++) {
                    if (f.value[index] === '') {
                        f.value[index] = SelectFilterComponent.blank;
                        break;
                    }
                }
            }

            if (f.group !== curGroup) {
                curGroup = f.group;

                if (curGroup) {
                    // New group - assign length and NOT (which only exist on the first field in a group)
                    newField.groupNot = f.not;
                    newField.groupLength = json.fields.filter(grouped => grouped.group === curGroup).length;
                    this.groupCnt += newField.groupLength;
                    this.notGroupCnt = f.not ? this.notGroupCnt + 1 : this.notGroupCnt;
                }
            }

            this.addField(newField);
        });

        this.setTitleColspans();
    }

    public areDatesValid(): boolean {
        return !this.selectedFields.filter(f => f.comparer !== FilterComparerGuids.IsBlank).filter(f => {
			return (f.field.fieldId === BuiltInFieldId.DocDate ||
				f.field.fieldId === BuiltInFieldId.MasterDate ||
				f.field.fieldId === BuiltInFieldId.DateTimeUploaded) &&
				(f.comparer === FilterComparerGuids.Between || f.comparer === FilterComparerGuids.NotBetween);
		}).some(f => !f.value[0] || !f.value[1]);
    }

    public serialize(): SavedFilterField[] {
        return this.selectedFields.filter((f,idx) => {
            switch (f.field.displayType) {
                case FieldDisplayType.MultiSelect:
                case FieldDisplayType.StaticDropdown:
                case FieldDisplayType.DateRange:
                    if (f.comparer === FilterComparerGuids.IsBlank) {
                        return true;
                    }
                    return f.value && f.value.length > 0 && f.value.some(v => v != null);
                case FieldDisplayType.Bates:
                case FieldDisplayType.ControlNumber:
                    return f.value && f.value.length > 0 && f.value.some(v => v != null);
                case FieldDisplayType.SearchReport:
                    if (f.value.length > 0)// If search Reports are selected, use unique filter value
                        f.value.unshift(this.selectedReportUniqueFilterHits.has(this.formatSearchReportUniqueKey(f.field.fieldId,idx)))
                    return f.value && f.value.length > 0 && f.value.some(v => v != null);
                default:
                    return true;
            }
        }).map(f => SavedFilterFieldFactory.createFromAdvanced(f));
    }

    public isFieldSelected(fieldId: string, index: number): boolean {
		return this.selectedReportUniqueFilterHits.has(this.formatSearchReportUniqueKey(fieldId, index));
	}

	public toggleFieldSelection(fieldId: string, index: number, isSelected: boolean): void {
		if (isSelected && !this.selectedReportUniqueFilterHits.has(this.formatSearchReportUniqueKey(fieldId, index))) {
			this.selectedReportUniqueFilterHits.add(this.formatSearchReportUniqueKey(fieldId, index));
		} else if (!isSelected && this.selectedReportUniqueFilterHits.has(this.formatSearchReportUniqueKey(fieldId, index))) {
			this.selectedReportUniqueFilterHits.delete(this.formatSearchReportUniqueKey(fieldId, index));
		}
	}

    public seedUniqueFilterHits(searchReportIds: Set<string>): void {
        searchReportIds.forEach(id => this.selectedReportUniqueFilterHits.add(id));
    }

    private adjustGroupLength(group: string, adjustment: number): number {
        const fieldIdx = this.selectedFields.findIndex(f => f && f.group === group && f.groupLength);
        if (fieldIdx !== -1) this.selectedFields[fieldIdx].groupLength += adjustment;
        return fieldIdx;
    }

    private setTitleColspans() {
        // Find the widest group for fields that are not part of a group
        let minColspanNoGroup = 1;
        this.selectedFields.forEach(field => {
            if (!field) return; // Field search
            if (field.groupLength) minColspanNoGroup = Math.max(minColspanNoGroup, 2);
            if (field.groupNot) minColspanNoGroup = Math.max(minColspanNoGroup, 3);
        });

        let curGroup: string;
        let curGroupNot: boolean;
        this.selectedFields.forEach(field => {
            if (!field) return; // Field Search

            if (field.group !== curGroup) {
                // Group is changing
                curGroup = field.group;
                curGroupNot = field.groupNot;
            }

            if (field.group) {
                if (curGroupNot || minColspanNoGroup !== 3) field.titleColspan = 1;
                else field.titleColspan = 2;
            } else {
                field.titleColspan = minColspanNoGroup;
            }
        });
    }

    private formatSearchReportUniqueKey(fieldId: string, index: number): string{
        return `${fieldId}_${index}`;
    }
}
